"use client";

import { useCallback, useState, useRef, useEffect } from "react";
import { Button, useToast } from "@boostdev/design-system-components/client";
import { ShareIcon } from "@/components/atoms/icons/ShareIcon";
import css from "./ShareButton.module.css";

export interface ShareButtonLabels {
  readonly label: string;
  readonly copied: string;
  readonly copyManually: string;
  readonly fallbackInstructions: string;
}

export interface ShareButtonProps {
  readonly labels: ShareButtonLabels;
  readonly className?: string;
}

/**
 * Copies the current page URL to the clipboard on click. On success, fires a
 * DS toast; on failure (permissions denied, insecure context, unsupported),
 * renders an inline readonly-input fallback so the URL is still selectable.
 *
 * The URL is read at click time from window.location.href — the canonical
 * photo-code URL (per research §5) — so the component doesn't need any prop
 * beyond its labels.
 */
export function ShareButton({ labels, className }: ShareButtonProps) {
  const { showToast } = useToast();
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(async () => {
    const url =
      typeof window !== "undefined" ? window.location.href : "";
    if (!navigator.clipboard || !window.isSecureContext) {
      setFallbackUrl(url);
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast(labels.copied, "success");
    } catch {
      setFallbackUrl(url);
    }
  }, [labels.copied, showToast]);

  // When the fallback appears, select its contents so Cmd/Ctrl-C is a
  // one-keypress operation.
  useEffect(() => {
    if (fallbackUrl && inputRef.current) {
      inputRef.current.select();
    }
  }, [fallbackUrl]);

  return (
    <div className={css.wrapper}>
      <Button
        isIconOnly
        onClick={handleClick}
        aria-label={labels.label}
        className={className}
      >
        <ShareIcon />
      </Button>
      {fallbackUrl ? (
        <div className={css.fallback} role="status">
          <p className={css.fallbackMessage}>{labels.copyManually}</p>
          <label className={css.fallbackLabel}>
            <span className="sr-only">{labels.fallbackInstructions}</span>
            <input
              ref={inputRef}
              type="text"
              readOnly
              value={fallbackUrl}
              className={css.fallbackInput}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}
